(() => {
  'use strict';

  const overlay = document.querySelector('.js-bulk-overlay');
  const drawer = document.querySelector('.js-bulk-drawer');
  if (!overlay || !drawer) return;

  // Drawer starts closed: keep it out of the tab order / a11y tree until opened.
  drawer.inert = true;
  drawer.setAttribute('aria-hidden', 'true');

  // ── Focus trap ──────────────────────────────────────────────────────────
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let previouslyFocused = null;

  function getFocusableElements() {
    return Array.from(drawer.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null,
    );
  }

  function handleKeydown(event) {
    // Detach if the drawer was closed by some other path.
    if (!drawer.classList.contains('js-bulk-open')) {
      document.removeEventListener('keydown', handleKeydown);
      return;
    }

    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !drawer.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !drawer.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  // ── Drawer open / close ─────────────────────────────────────────────────
  function openDrawer() {
    previouslyFocused = document.activeElement;
    overlay.classList.add('js-bulk-open');
    drawer.classList.add('js-bulk-open');
    document.body.style.overflow = 'hidden';

    drawer.inert = false;
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', handleKeydown);

    const focusable = getFocusableElements();
    (focusable[0] || drawer).focus();
  }

  function closeDrawer() {
    overlay.classList.remove('js-bulk-open');
    drawer.classList.remove('js-bulk-open');
    document.body.style.overflow = '';

    document.removeEventListener('keydown', handleKeydown);

    // Move focus out before making the drawer inert (focus inside an inert
    // element is dropped by the browser).
    if (previouslyFocused && previouslyFocused.focus) {
      previouslyFocused.focus();
      previouslyFocused = null;
    }

    drawer.inert = true;
    drawer.setAttribute('aria-modal', 'false');
    drawer.setAttribute('aria-hidden', 'true');
  }

  // ── DOM refs ────────────────────────────────────────────────────────────
  const form = drawer.querySelector('#js-bulk-form');
  const bodyInput = drawer.querySelector('.js-bulk-body');
  const emailInput = drawer.querySelector('.js-bulk-email');
  const submitBtn = drawer.querySelector('.js-bulk-submit');
  const submitTxt = drawer.querySelector('.js-bulk-submit-text');
  const submitSpin = drawer.querySelector('.js-bulk-submit-spinner');
  const formState = drawer.querySelector('.js-bulk-form-state');
  const successState = drawer.querySelector('.js-bulk-success-state');

  // ── Shopify contact form ────────────────────────────────────────────────
  // POST the serialized <form> to /contact. When Shopify spam protection is on,
  // the form must already contain the injected `h-captcha-response` token —
  // this is called from inside Shopify.captcha.protect()'s callback (see submit
  // handler below), which guarantees the token is present at serialization time.
  async function postContactForm() {
    try {
      const res = await fetch('/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: new URLSearchParams(new FormData(form)).toString(),
      });
      return res.ok;
    } catch (err) {
      console.error('[BulkCTA] Shopify contact submit failed:', err.message);
      return false;
    }
  }

  // ── Reset to initial state ──────────────────────────────────────────────
  function resetDrawer() {
    if (emailInput) emailInput.value = '';
    if (submitBtn) submitBtn.disabled = true;
    formState?.classList.remove('hidden');
    successState?.classList.add('hidden');
  }

  // ── Bind events ─────────────────────────────────────────────────────────
  function bindEvents() {
    // Open triggers (any .js-bulk-cta-open on the page)
    document.querySelectorAll('.js-bulk-cta-open').forEach((btn) => {
      btn.addEventListener('click', openDrawer);
    });

    // Close button
    drawer
      .querySelector('.js-bulk-close')
      ?.addEventListener('click', closeDrawer);

    // Overlay backdrop click
    overlay.addEventListener('click', closeDrawer);

    // Email validation → enable submit
    emailInput?.addEventListener('input', () => {
      if (submitBtn) {
        submitBtn.disabled = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          emailInput.value.trim(),
        );
      }
    });

    // Submit (form-level so Shopify's hCaptcha wiring applies)
    form?.addEventListener('submit', (e) => {
      e.preventDefault();

      const email = emailInput?.value.trim();
      if (!email) return;

      submitBtn.disabled = true;
      submitTxt?.classList.add('hidden');
      submitSpin?.classList.remove('hidden');

      // Include the customer email in the message body.
      if (bodyInput) {
        bodyInput.value = `Bulk order inquiry submitted from the product page. Customer email: ${email}`;
      }

      const finish = () => {
        submitTxt?.classList.remove('hidden');
        submitSpin?.classList.add('hidden');
        // Show success regardless of API outcome
        formState?.classList.add('hidden');
        successState?.classList.remove('hidden');
      };

      const captcha = window.Shopify?.captcha;
      if (captcha && typeof captcha.protect === 'function') {
        // Shopify injects the `h-captcha-response` token into the form, then
        // runs this callback — at which point postContactForm() serializes the
        // form (token included) and submits via AJAX.
        captcha.protect(form, () => {
          postContactForm().finally(finish);
        });
      } else {
        // Spam protection not enabled (or library unavailable) — submit directly.
        postContactForm().finally(finish);
      }
    });
  }

  // Reset state whenever drawer reopens
  new MutationObserver(() => {
    if (overlay.classList.contains('js-bulk-open')) resetDrawer();
  }).observe(overlay, { attributes: true, attributeFilter: ['class'] });

  document.addEventListener('DOMContentLoaded', bindEvents);
})();
